/**
 * One question of the survey question editor (plan-030 WAPP-Q; design §41.10): a card with the question's position,
 * type, code and wording that opens to its fields — the wording and its clarification, a scale question's own scale
 * words, a choice question's options and how many may be picked, a number question's range and unit — with Move up,
 * Move down and Remove as buttons (keyboard reachable; no drag-only control) and "Add a question below".
 *
 * Problems are located: each field shows the problems whose path names it in the shown language, and the card lists
 * every problem of its question (another language's too, named) so nothing a save refuses is out of sight. Words wetalk
 * added (OD-7) carry a "check the wording" chip until the owner edits them.
 */
import { Button, Field, Icon, IconButton, Input, Tag, Textarea } from "@wetalk/ui";
import type { ReactNode, Ref } from "react";

import { GLYPH } from "../../../../shell/glyph.js";
import { usePortalTranslation } from "../../../../i18n/portal.js";
import type { QuestionnaireNoteView } from "../../../../api/slice/survey-questionnaire.js";
import type { NoteLocation, QuestionShape, QuestionWordingKey } from "./questionnaire-model.js";
import { editorText, languageLabel, problemText, questionTypeLabel, warningText } from "./questionnaire-copy.js";

/** A problem or warning with where it is. */
export interface LocatedNote {
  readonly note: QuestionnaireNoteView;
  readonly location: NoteLocation;
}

/** What a card changes; the tab turns each into an edit of the map. */
export interface QuestionCardEdit {
  readonly wording: (key: QuestionWordingKey, value: string) => void;
  readonly code: (code: string) => void;
  readonly selection: (change: { readonly min?: number; readonly max?: number; readonly exclusive?: readonly string[] }) => void;
  readonly more: (value: string) => void;
  readonly range: (change: { readonly min?: number | null; readonly max?: number | null; readonly integer?: boolean }) => void;
  readonly optionAdded: () => void;
  readonly optionRemoved: (optionIndex: number) => void;
  readonly optionLabel: (optionIndex: number, label: string) => void;
  readonly optionKeywords: (optionIndex: number, line: string) => void;
  readonly moved: (step: -1 | 1) => void;
  readonly removed: () => void;
  readonly addBelow: () => void;
}

export interface QuestionCardProps {
  readonly position: number;
  readonly count: number;
  readonly question: QuestionShape;
  readonly language: string;
  /** Read-only: the switch is off, another version is shown, or the VoiceAgent is archived. */
  readonly readOnly: boolean;
  readonly open: boolean;
  readonly onOpen: (open: boolean) => void;
  /** The question is new to this edit, so its code may still be chosen. */
  readonly isNew: boolean;
  readonly problem: readonly LocatedNote[];
  readonly warning: readonly LocatedNote[];
  /** `true` for a field whose words wetalk added and the owner has not edited yet. */
  readonly wordingAdded: (field: string) => boolean;
  readonly edit: QuestionCardEdit;
  /** The card's heading, focused when a problem jump or a move lands on it. */
  readonly headingRef?: Ref<HTMLHeadingElement>;
  readonly moveUpRef?: Ref<HTMLButtonElement>;
  readonly moveDownRef?: Ref<HTMLButtonElement>;
}

function numberOrNull(raw: string): number | null {
  if (raw.trim() === "") return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

/** The fields an open card draws for a question, as a pattern over a note's field path. */
function drawnFieldOf(question: QuestionShape, isNew: boolean): RegExp {
  const own =
    question.type === "scale"
      ? ["suffix"]
      : question.type === "choice"
        ? ["clarify", "options", "options\\.\\d+\\.(label|keywords)", "selection", "selection\\.more"]
        : question.type === "number"
          ? ["clarify", "min", "max", "unit"]
          : ["clarify"];
  return new RegExp(`^(${[...(isNew ? ["id"] : []), "text", ...own].join("|")})$`, "u");
}

/** The problems of the shown language located at `field` (an option's field also matches `options`). */
function fieldProblem(problem: readonly LocatedNote[], language: string, field: string): string | undefined {
  const found = problem.filter((one) => one.location.language === language && one.location.field === field);
  return found.length === 0 ? undefined : found.map((one) => problemText(one.note)).join(" ");
}

export function QuestionCard({ headingRef, moveUpRef, moveDownRef, ...props }: QuestionCardProps): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const { question, language, edit, readOnly } = props;
  const t = editorText;
  const errorAt = (field: string): { readonly error?: string } => {
    const message = fieldProblem(props.problem, language, field);
    return message === undefined ? {} : { error: message };
  };
  const chip = (field: string): ReactNode =>
    props.wordingAdded(field) ? (
      <span className="c-qn-chip" role="note">
        <Icon glyph={GLYPH.warningCircle} size="sm" />
        {t("chip.check_wording")}
      </span>
    ) : null;
  const elsewhere = props.problem.filter((one) => one.location.language !== language);
  const here = props.problem.filter((one) => one.location.language === language);
  // An open card shows each problem on its field; the ones no drawn field holds are listed under the fields.
  const unplaced = here.filter((one) => !drawnFieldOf(question, props.isNew).test(one.location.field));
  const titleText = question.text.trim() === "" ? t("card.no_wording") : question.text;

  return (
    <li className="c-qn-card c-va-card" data-problem={props.problem.length > 0 ? "true" : "false"} data-question-code={question.id}>
      <div className="c-qn-card-head">
        <span className="c-qn-position" aria-hidden="true">
          {String(props.position + 1)}
        </span>
        <div className="c-qn-card-copy">
          <h3 className="c-qn-card-title" tabIndex={-1} ref={headingRef}>
            <span className="c-qn-card-wording">{titleText}</span>
          </h3>
          <p className="c-qn-card-meta">
            <Tag tone="neutral">{questionTypeLabel(question.type)}</Tag>
            <span className="c-qn-code">{question.id}</span>
            {props.problem.length === 0 ? null : (
              <Tag status="critical">{t("card.problem_count", { count: props.problem.length })}</Tag>
            )}
          </p>
        </div>
        {readOnly ? null : (
          <div className="c-qn-card-actions">
            <Button
              variant="ghost"
              aria-expanded={props.open}
              onClick={() => {
                props.onOpen(!props.open);
              }}
            >
              <Icon glyph={props.open ? GLYPH.caretUp : GLYPH.pencilSimple} size="sm" />
              <span>{props.open ? t("card.done") : t("card.edit")}</span>
            </Button>
            {/* IconButton's own markup, with a ref: focus stays on the button that moved the card. */}
            <button
              type="button"
              className="btn btn-icon"
              aria-label={t("card.move_up", { code: question.id })}
              disabled={props.position === 0}
              ref={moveUpRef}
              onClick={() => {
                edit.moved(-1);
              }}
            >
              <Icon glyph={GLYPH.caretUp} size="md" />
            </button>
            <button
              type="button"
              className="btn btn-icon"
              aria-label={t("card.move_down", { code: question.id })}
              disabled={props.position === props.count - 1}
              ref={moveDownRef}
              onClick={() => {
                edit.moved(1);
              }}
            >
              <Icon glyph={GLYPH.caretDown} size="md" />
            </button>
            <IconButton
              glyph={GLYPH.trash}
              label={t("card.remove", { code: question.id })}
              disabled={props.count === 1}
              onClick={edit.removed}
            />
          </div>
        )}
      </div>

      {props.problem.length === 0 || props.open ? null : (
        <ul className="c-qn-notes" aria-label={t("card.problems_of", { code: question.id })}>
          {here.map((one, index) => (
            <li key={`h${String(index)}`} className="c-qn-note c-qn-note-problem">
              {problemText(one.note)}
            </li>
          ))}
        </ul>
      )}
      {elsewhere.length === 0 ? null : (
        <ul className="c-qn-notes" aria-label={t("card.problems_elsewhere", { code: question.id })}>
          {elsewhere.map((one, index) => (
            <li key={`e${String(index)}`} className="c-qn-note c-qn-note-problem">
              {t("card.in_language", { language: languageLabel(one.location.language ?? ""), text: problemText(one.note) })}
            </li>
          ))}
        </ul>
      )}
      {props.warning.length === 0 ? null : (
        <ul className="c-qn-notes" aria-label={t("card.warnings_of", { code: question.id })}>
          {props.warning.map((one, index) => (
            <li key={`w${String(index)}`} className="c-qn-note c-qn-note-warning">
              {warningText(one.note)}
            </li>
          ))}
        </ul>
      )}

      {!props.open || readOnly ? (
        readOnly ? <ReadOnlyBody question={question} /> : null
      ) : (
        <div className="c-qn-fields">
          {props.isNew ? (
            <Field label={t("field.code")} description={t("field.code_hint")} {...errorAt("id")}>
              {(control) => (
                <Input
                  {...control}
                  font="mono"
                  value={question.id}
                  onChange={(event) => {
                    edit.code(event.currentTarget.value.toUpperCase());
                  }}
                />
              )}
            </Field>
          ) : null}
          <Field label={t("field.wording")} description={t("field.wording_hint")} {...errorAt("text")}>
            {(control) => (
              <Textarea
                {...control}
                value={question.text}
                onChange={(event) => {
                  edit.wording("text", event.currentTarget.value);
                }}
              />
            )}
          </Field>
          {question.type === "scale" ? (
            <Field label={t("field.suffix")} description={t("field.suffix_hint")} {...errorAt("suffix")}>
              {(control) => (
                <Input
                  {...control}
                  value={question.suffix}
                  onChange={(event) => {
                    edit.wording("suffix", event.currentTarget.value);
                  }}
                />
              )}
            </Field>
          ) : (
            <div className="c-qn-field-with-chip">
              <Field label={t("field.clarify")} description={t("field.clarify_hint")} {...errorAt("clarify")}>
                {(control) => (
                  <Input
                    {...control}
                    value={question.clarify}
                    onChange={(event) => {
                      edit.wording("clarify", event.currentTarget.value);
                    }}
                  />
                )}
              </Field>
              {chip("clarify")}
            </div>
          )}

          {question.type === "choice" ? (
            <ChoiceFields question={question} errorAt={errorAt} chip={chip} edit={edit} />
          ) : null}
          {question.type === "number" ? <NumberFields question={question} errorAt={errorAt} edit={edit} /> : null}

          {unplaced.length === 0 ? null : (
            <ul className="c-qn-notes" aria-label={t("card.problems_of", { code: question.id })}>
              {unplaced.map((one, index) => (
                <li key={`f${String(index)}`} className="c-qn-note c-qn-note-problem">
                  {problemText(one.note)}
                </li>
              ))}
            </ul>
          )}

          <div className="c-qn-card-foot">
            <Button variant="ghost" onClick={edit.addBelow}>
              <Icon glyph={GLYPH.plus} size="sm" />
              <span>{t("card.add_below")}</span>
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function ChoiceFields({
  question,
  errorAt,
  chip,
  edit,
}: {
  readonly question: QuestionShape;
  readonly errorAt: (field: string) => { readonly error?: string };
  readonly chip: (field: string) => ReactNode;
  readonly edit: QuestionCardEdit;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const t = editorText;
  const optionsError = errorAt("options");
  const selectionError = errorAt("selection");
  return (
    <fieldset className="c-qn-group">
      <legend className="c-qn-group-title">{t("field.options")}</legend>
      {optionsError.error === undefined ? null : (
        <p className="c-qn-note c-qn-note-problem" role="alert">
          {optionsError.error}
        </p>
      )}
      <ol className="c-qn-options">
        {question.option.map((option, optionIndex) => (
          <li key={option.id === "" ? `o${String(optionIndex)}` : option.id} className="c-qn-option">
            <Field label={t("field.option", { number: optionIndex + 1 })} {...errorAt(`options.${String(optionIndex)}.label`)}>
              {(control) => (
                <Input
                  {...control}
                  value={option.label}
                  onChange={(event) => {
                    edit.optionLabel(optionIndex, event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <Field
              label={t("field.keywords")}
              description={t("field.keywords_hint")}
              {...errorAt(`options.${String(optionIndex)}.keywords`)}
            >
              {(control) => (
                <Input
                  {...control}
                  value={option.keywords.join(", ")}
                  onChange={(event) => {
                    edit.optionKeywords(optionIndex, event.currentTarget.value);
                  }}
                />
              )}
            </Field>
            <label className="c-qn-check">
              <input
                type="checkbox"
                checked={question.exclusive.includes(option.id)}
                onChange={(event) => {
                  const on = event.currentTarget.checked;
                  edit.selection({
                    exclusive: on ? [...question.exclusive, option.id] : question.exclusive.filter((one) => one !== option.id),
                  });
                }}
              />
              <span>{t("field.exclusive")}</span>
            </label>
            <IconButton
              glyph={GLYPH.trash}
              label={t("field.remove_option", { number: optionIndex + 1 })}
              disabled={question.option.length === 1}
              onClick={() => {
                edit.optionRemoved(optionIndex);
              }}
            />
          </li>
        ))}
      </ol>
      <Button variant="secondary" onClick={edit.optionAdded}>
        <Icon glyph={GLYPH.plus} size="sm" />
        <span>{t("field.add_option")}</span>
      </Button>
      <div className="c-qn-row">
        <Field label={t("field.selection_min")} {...selectionError}>
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              value={String(question.selectionMin)}
              onChange={(event) => {
                const value = numberOrNull(event.currentTarget.value);
                if (value !== null) edit.selection({ min: value });
              }}
            />
          )}
        </Field>
        <Field label={t("field.selection_max")}>
          {(control) => (
            <Input
              {...control}
              type="number"
              min={1}
              value={String(question.selectionMax)}
              onChange={(event) => {
                const value = numberOrNull(event.currentTarget.value);
                if (value !== null) edit.selection({ max: value });
              }}
            />
          )}
        </Field>
      </div>
      {question.selectionMax > 1 ? (
        <div className="c-qn-field-with-chip">
          <Field label={t("field.more")} description={t("field.more_hint")} {...errorAt("selection.more")}>
            {(control) => (
              <Input
                {...control}
                value={question.more}
                onChange={(event) => {
                  edit.more(event.currentTarget.value);
                }}
              />
            )}
          </Field>
          {chip("selection.more")}
        </div>
      ) : null}
    </fieldset>
  );
}

function NumberFields({
  question,
  errorAt,
  edit,
}: {
  readonly question: QuestionShape;
  readonly errorAt: (field: string) => { readonly error?: string };
  readonly edit: QuestionCardEdit;
}): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const t = editorText;
  return (
    <fieldset className="c-qn-group">
      <legend className="c-qn-group-title">{t("field.range")}</legend>
      <div className="c-qn-row">
        <Field label={t("field.number_min")} {...errorAt("min")}>
          {(control) => (
            <Input
              {...control}
              type="number"
              value={question.min === null ? "" : String(question.min)}
              onChange={(event) => {
                edit.range({ min: numberOrNull(event.currentTarget.value) });
              }}
            />
          )}
        </Field>
        <Field label={t("field.number_max")} {...errorAt("max")}>
          {(control) => (
            <Input
              {...control}
              type="number"
              value={question.max === null ? "" : String(question.max)}
              onChange={(event) => {
                edit.range({ max: numberOrNull(event.currentTarget.value) });
              }}
            />
          )}
        </Field>
        <Field label={t("field.unit")} {...errorAt("unit")}>
          {(control) => (
            <Input
              {...control}
              value={question.unit}
              onChange={(event) => {
                edit.wording("unit", event.currentTarget.value);
              }}
            />
          )}
        </Field>
      </div>
      <label className="c-qn-check">
        <input
          type="checkbox"
          checked={question.integer}
          onChange={(event) => {
            edit.range({ integer: event.currentTarget.checked });
          }}
        />
        <span>{t("field.integer")}</span>
      </label>
    </fieldset>
  );
}

/** A question as it is, for a read-only view: its options or its range under the wording. */
function ReadOnlyBody({ question }: { readonly question: QuestionShape }): ReactNode {
  usePortalTranslation("surveyQuestionnaire");
  const t = editorText;
  if (question.type === "choice") {
    return (
      <ul className="c-qn-readonly-options">
        {question.option.map((option) => (
          <li key={option.id}>{option.label}</li>
        ))}
      </ul>
    );
  }
  if (question.type === "number") {
    return (
      <p className="c-qn-readonly-range">
        {question.min === null && question.max === null
          ? t("readonly.any_number")
          : t("readonly.range", {
              min: question.min === null ? "—" : String(question.min),
              max: question.max === null ? "—" : String(question.max),
            })}
        {question.unit === "" ? null : ` · ${question.unit}`}
      </p>
    );
  }
  return null;
}
